import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from '@hookform/resolvers/zod';
import Text from "@/components/forms/text";
import SmallSpinner from "@/components/shared/spinners/small-spinner";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Phone, Save, Info } from 'lucide-react';
import useIsPWA from '@/hooks/use-is-pwa';
import { useChangeContact } from '@/hooks/api/use-amendments';
import { useCurrentUser } from '@/hooks/api/use-user';
import { changeInformationsSchema, type ChangeInformationsValues } from '@/schemas/change-informations';

const ChangeInformations = () => {
    const user = useCurrentUser();
    const postAmendmentChangeContact = useChangeContact<ChangeInformationsValues>();
    const loading = postAmendmentChangeContact.isPending;
    const isPWA = useIsPWA();
    const navigate = useNavigate();

    const { register, handleSubmit, watch, formState: { errors } } = useForm<ChangeInformationsValues>({
        defaultValues: {
            tiers: user.belair_id,
            tel_mobile: user.address_phone,
            email: user.email,
            email_confirmation: '',
        },
        resolver: zodResolver(changeInformationsSchema) as unknown as Resolver<ChangeInformationsValues>,
    });

    return (
        <div className="space-y-6">
            <div className="space-y-2 sm:space-y-3 mt-4">
                <h1 className="text-xl sm:text-2xl font-bold">Je modifie mes informations de contact</h1>
            </div>

            <form onSubmit={handleSubmit(values => postAmendmentChangeContact.mutate({ tiersId: user.belair_id as string | number, form: values, successFunction: () => { void navigate('/mon-profil'); } }))}>
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Phone className="w-5 h-5 text-primary" />
                            Informations de contact
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-5">
                        <div className="max-w-md">
                            <Text
                                type={'text'}
                                label={'Numéro de téléphone'}
                                {...register('tel_mobile')}
                                value={watch('tel_mobile')}
                                error={errors.tel_mobile?.message}
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Text
                                type={'text'}
                                autoComplete="email"
                                label={'Adresse e-mail de contact'}
                                {...register('email')}
                                value={watch('email')}
                                error={errors.email?.message}
                            />
                            <Text
                                type={'text'}
                                autoComplete="email"
                                label={'Confirmez votre adresse e-mail'}
                                {...register('email_confirmation')}
                                value={watch('email_confirmation')}
                                error={errors.email_confirmation?.message}
                            />
                        </div>

                        <Alert className="bg-[#dfe6eb] border-[#dfe6eb]">
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                                Cette adresse sera utilisée pour confirmer tous les actes effectués dans votre Espace Client. Il est donc indispensable qu'elle soit valide.
                            </AlertDescription>
                        </Alert>

                        <div className="max-w-md">
                            <Text
                                type={'password'}
                                label={'Votre mot de passe'}
                                {...register('password')}
                                value={watch('password')}
                                error={errors.password?.message}
                                required
                            />
                        </div>

                        <div className="pt-2">
                            {loading ? (
                                <SmallSpinner />
                            ) : (
                                <Button type="submit" className="w-full text-white cursor-pointer whitespace-normal py-6">
                                    <Save className={isPWA ? "w-5 h-5 shrink-0" : "w-4 h-4 shrink-0"} />
                                    Je valide mes informations personnelles
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </form>
        </div>
    );
};

export default ChangeInformations;
